import {
    Container,
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    Button,
    FormGroup,
    Label,
    Input,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Spinner,
    Table,
    Pagination,
    PaginationItem,
    PaginationLink,
    Nav, // Add Nav here
    NavItem, // Add NavItem here
    NavLink, // Add NavLink here
} from 'reactstrap';
import { useState, useEffect } from 'react';
import { FaPlus, FaEdit } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { apiURL1 } from '../../config';
import Footer from 'components/Footers/Footer';
import TeacherAttendance from './AdminTeacherAtt';

const StudentAttendance = ({ filterDate, filterName, filterClass, classes, onEditAttendance, refreshAttendance }) => {
    const [attendanceData, setAttendanceData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const token = localStorage.getItem('token');

    // Reset to first page when filters change
    useEffect(() => {
        setPage(1);
    }, [filterDate, filterName, filterClass]);

    useEffect(() => {
        fetchAttendance();
    }, [filterDate, filterName, filterClass, page, refreshAttendance]);

    const fetchAttendance = async () => {
        setLoading(true);
        try {
            const params = { page, limit: 20 };
            if (filterDate) params.date = filterDate;
            if (filterName) params.name = filterName;
            if (filterClass) params.class = filterClass;

            const response = await axios.get(`${apiURL1}/superadmin/getStudentAttendance`, {
                headers: { Authorization: `Bearer ${token}` },
                params,
            });

            setAttendanceData(response.data.data || []);
            setTotalPages(Math.max(1, response.data.totalPages || 1));
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error fetching student attendance!');
            setAttendanceData([]);
            setTotalPages(1);
        } finally {
            setLoading(false);
        }
    };

    const formatTime = (isoTime) => {
        if (!isoTime) return '-';
        const date = new Date(isoTime);
        return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    };

    // Generate pagination items
    const getPaginationItems = () => {
        const items = [];
        const maxPagesToShow = 5; // Number of pages to show before/after current page
        const startPage = Math.max(1, page - 2);
        const endPage = Math.min(totalPages, page + 2);

        // Previous button
        items.push(
            <PaginationItem disabled={page === 1} key="prev">
                <PaginationLink previous onClick={() => setPage(page - 1)} />
            </PaginationItem>
        );

        // First page
        if (startPage > 1) {
            items.push(
                <PaginationItem active={page === 1} key={1}>
                    <PaginationLink onClick={() => setPage(1)}>1</PaginationLink>
                </PaginationItem>
            );
        }

        // Ellipsis before
        if (startPage > 2) {
            items.push(
                <PaginationItem disabled key="start-ellipsis">
                    <PaginationLink>...</PaginationLink>
                </PaginationItem>
            );
        }

        // Middle pages
        for (let p = startPage; p <= endPage; p++) {
            items.push(
                <PaginationItem active={page === p} key={p}>
                    <PaginationLink onClick={() => setPage(p)}>{p}</PaginationLink>
                </PaginationItem>
            );
        }

        // Ellipsis after
        if (endPage < totalPages - 1) {
            items.push(
                <PaginationItem disabled key="end-ellipsis">
                    <PaginationLink>...</PaginationLink>
                </PaginationItem>
            );
        }

        // Last page
        if (endPage < totalPages) {
            items.push(
                <PaginationItem active={page === totalPages} key={totalPages}>
                    <PaginationLink onClick={() => setPage(totalPages)}>{totalPages}</PaginationLink>
                </PaginationItem>
            );
        }

        // Next button
        items.push(
            <PaginationItem disabled={page === totalPages} key="next">
                <PaginationLink next onClick={() => setPage(page + 1)} />
            </PaginationItem>
        );

        return items;
    };

    return (
        <>
            {loading ? (
                <Spinner className="my-5 mx-auto" />
            ) : (
                <>
                    <Table responsive>
                        <thead>
                            <tr>
                                <th>Student Name</th>
                                <th>Registration No</th>
                                <th>Class</th>
                                <th>Date</th>
                                <th>Check In</th>
                                <th>Check Out</th>
                                <th>Status</th>
                                <th>Remarks</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {attendanceData.length > 0 ? (
                                attendanceData.map((record) => (
                                    <tr key={`${record.studentId}-${record.date}`}>
                                        <td>{record.fullName || 'Unknown'}</td>
                                        <td>{record.registrationNumber || '-'}</td>
                                        <td>{record.class || 'Unassigned'}</td>
                                        <td>{record.date || '-'}</td>
                                        <td>{formatTime(record.checkIn)}</td>
                                        <td>{formatTime(record.checkOut)}</td>
                                        <td>{record.status || '-'}</td>
                                        <td>{record.remarks || 'None'}</td>
                                        <td>
                                            <Button
                                                color="warning"
                                                size="sm"
                                                onClick={() =>
                                                    onEditAttendance({
                                                        studentId: record.studentId,
                                                        date: record.date,
                                                        status: record.status,
                                                        checkIn: record.checkIn,
                                                        checkOut: record.checkOut,
                                                    })
                                                }
                                                disabled={record.remarks === ''}
                                                title={record.remarks === '' ? 'Cannot edit fingerprint-based records' : ''}
                                            >
                                                <FaEdit /> Edit
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="9" className="text-center">
                                        No attendance records found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                    {totalPages > 1 && (
                        <div className="d-flex justify-content-end mt-3">
                            <Pagination className="mb-0">
                                {getPaginationItems()}
                            </Pagination>
                        </div>
                    )}
                </>
            )}
        </>
    );
};

const AttendanceOverview = () => {
    const navigate = useNavigate();
    const [view, setView] = useState('teacher');
    const [filterDate, setFilterDate] = useState('');
    const [filterName, setFilterName] = useState('');
    const [filterClass, setFilterClass] = useState('');
    const [classes, setClasses] = useState([]);
    const [addStudentAttendanceModalOpen, setAddStudentAttendanceModalOpen] = useState(false);
    const [addTeacherAttendanceModalOpen, setAddTeacherAttendanceModalOpen] = useState(false);
    const [activeTeacherTab, setActiveTeacherTab] = useState('full-time');
    const [newStudentAttendance, setNewStudentAttendance] = useState({
        studentId: '',
        date: '',
        status: 'Present',
        checkInTime: '',
        checkOutTime: '',
    });
    const [newTeacherAttendance, setNewTeacherAttendance] = useState({
        teacherId: '',
        date: '',
        status: 'Present',
        checkInTime: '',
        checkOutTime: '',
    });
    const [isEditing, setIsEditing] = useState(false);
    const [studentNames, setStudentNames] = useState([]);
    const [teacherNames, setTeacherNames] = useState([]);
    const token = localStorage.getItem('token');
    const [loading, setLoading] = useState(false);
    const [refreshAttendance, setRefreshAttendance] = useState(false);

    useEffect(() => {
        fetchStudentNames();
        fetchTeacherNames();
        fetchClasses();
    }, []);

    useEffect(() => {
        if (refreshAttendance) {
            setRefreshAttendance(false);
        }
    }, [refreshAttendance]);

    const fetchStudentNames = async () => {
        try {
            const response = await axios.get(`${apiURL1}/superadmin/getStudentsName`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { limit: 1000 },
            });
            // console.log('getStudentsName response:', response.data);
            setStudentNames(
                response.data.students?.map((student) => ({
                    _id: student._id,
                    fullName: student.fullName || 'Unknown',
                })) || []
            );
            if (!response.data.students?.length) {
                toast.warn('No students found.');
            }
        } catch (error) {
            // console.error('Error fetching student names:', error.response?.data || error);
            toast.error(error.response?.data?.message || 'Error fetching student names!');
            setStudentNames([]);
        }
    };

    const fetchTeacherNames = async () => {
        try {
            const response = await axios.get(`${apiURL1}/superadmin/getTeachersName`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { limit: 1000 },
            });
            setTeacherNames(
                response.data.teachers?.map((teacher) => ({
                    _id: teacher._id,
                    fullName: teacher.name || 'Unknown',
                })) || []
            );
            if (!response.data.teachers?.length) {
                toast.warn('No teachers found.');
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error fetching teacher names!');
            setTeacherNames([]);
        }
    };

    const fetchClasses = async () => {
        try {
            const response = await axios.get(`${apiURL1}/superadmin/getClassName`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            // console.log('getClassName response:', response.data);
            setClasses(response.data || []);
        } catch (error) {
            // console.error('Error fetching classes:', error.response?.data || error);
            toast.error(error.response?.data?.message || 'Error fetching classes!');
        }
    };

    const handleViewChange = (newView) => {
        setView(newView);
        setFilterDate('');
        setFilterName('');
        setFilterClass('');
    };

    const handleFilterDateChange = (e) => {
        const date = e.target.value;
        const today = new Date().toISOString().split('T')[0];
        // if (date > today) {
        //     toast.error('Future dates are not allowed.');
        //     return;
        // }
        setFilterDate(date);
    };

    const handleFilterNameChange = (e) => {
        setFilterName(e.target.value);
    };

    const handleClassSelect = (e) => {
        setFilterClass(e.target.value);
    };

    const handleAddAttendanceToggle = (record = null) => {
        if (view === 'student') {
            if (record) {
                setNewStudentAttendance({
                    studentId: record.studentId,
                    date: record.date,
                    status: record.status,
                });
                setIsEditing(true);
            } else {
                setNewStudentAttendance({ studentId: '', date: '', status: 'Present', checkInTime: '', checkOutTime: '' });
                setIsEditing(false);
            }
            setAddStudentAttendanceModalOpen(!addStudentAttendanceModalOpen);
        } else if (view === 'teacher') {
            if (record) {
                setNewTeacherAttendance({
                    teacherId: record.teacherId,
                    date: record.date,
                    status: record.status,
                });
                setIsEditing(true);
            } else {
                setNewTeacherAttendance({ teacherId: '', date: '', status: 'Present', checkInTime: '', checkOutTime: '' });
                setIsEditing(false);
            }
            setAddTeacherAttendanceModalOpen(!addTeacherAttendanceModalOpen);
        }
    };

    const handleNewStudentAttendanceChange = (e) => {
        setNewStudentAttendance({ ...newStudentAttendance, [e.target.name]: e.target.value });
    };

    const handleNewTeacherAttendanceChange = (e) => {
        setNewTeacherAttendance({ ...newTeacherAttendance, [e.target.name]: e.target.value });
    };

    const handleAddStudentAttendanceSubmit = async () => {
        setLoading(true);
        try {
            const { studentId, date, status, checkInTime, checkOutTime } = newStudentAttendance;

            if (!studentId || !date || !status) {
                toast.error('Please fill all required fields.');
                setLoading(false);
                return;
            }

            if (status !== 'Absent') {
                if (!checkInTime || !checkOutTime) {
                    toast.error('Please provide check-in and check-out times.');
                    setLoading(false);
                    return;
                }
                const inDt = new Date(`${date}T${checkInTime}`);
                const outDt = new Date(`${date}T${checkOutTime}`);
                if (!(inDt < outDt)) {
                    toast.error('Check-in time must be before check-out time.');
                    setLoading(false);
                    return;
                }
            }

            const today = new Date().toISOString().split('T')[0];
            if (date > today) {
                toast.error('Future dates are not allowed.');
                setLoading(false);
                return;
            }

            // Check for existing attendance
            const checkResponse = await axios.get(`${apiURL1}/superadmin/getStudentAttendance`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { studentId, date },
            });
            if (checkResponse.data.data?.length > 0 && !isEditing) {
                toast.error('Attendance already exists for this student on this date.');
                setLoading(false);
                return;
            }

            const response = await axios.post(
                `${apiURL1}/superadmin/addStudentAttendance`,
                { studentId, date, status, checkInTime: status !== 'Absent' ? checkInTime : undefined, checkOutTime: status !== 'Absent' ? checkOutTime : undefined },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                toast.success(response.data.message);
                setRefreshAttendance(true);
                handleAddAttendanceToggle();
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error adding student attendance!');
        }
        setLoading(false);
    };

    const handleAddTeacherAttendanceSubmit = async () => {
        setLoading(true);
        try {
            const { teacherId, date, status, checkInTime, checkOutTime } = newTeacherAttendance;

            if (!teacherId || !date || !status) {
                toast.error('Please fill all required fields.');
                setLoading(false);
                return;
            }

            if (status !== 'Absent') {
                if (!checkInTime || !checkOutTime) {
                    toast.error('Please provide check-in and check-out times.');
                    setLoading(false);
                    return;
                }
                const inDt = new Date(`${date}T${checkInTime}`);
                const outDt = new Date(`${date}T${checkOutTime}`);
                if (!(inDt < outDt)) {
                    toast.error('Check-in time must be before check-out time.');
                    setLoading(false);
                    return;
                }
            }

            const today = new Date().toISOString().split('T')[0];
            if (date > today) {
                toast.error('Future dates are not allowed.');
                setLoading(false);
                return;
            }

            // Check for existing attendance
            const checkResponse = await axios.get(`${apiURL1}/superadmin/getTeacherAttendance`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { teacherId, date },
            });
            if (checkResponse.data.data?.length > 0 && !isEditing) {
                toast.error('Attendance already exists for this teacher on this date.');
                setLoading(false);
                return;
            }

            const response = await axios.post(
                `${apiURL1}/superadmin/addTeacherAttendance`,
                { teacherId, date, status, checkInTime: status !== 'Absent' ? checkInTime : undefined, checkOutTime: status !== 'Absent' ? checkOutTime : undefined },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                toast.success(response.data.message);
                setRefreshAttendance(true);
                handleAddAttendanceToggle();
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error adding teacher attendance!');
        }
        setLoading(false);
    };

    return (
        <>
            <div
                className="header d-flex align-items-center"
                style={{
                    minHeight: '600px',
                    backgroundImage: `url(${require('../../assets/img/theme/alRehman.jpg')})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center top',
                }}
            >
                <span className="mask bg-gradient-default opacity-8" />
                <Container className="d-flex align-items-center" fluid>
                    <Row>
                        <Col lg="7" md="10">
                            <h1 className="display-2 text-white">Attendance Overview</h1>
                            <p className="text-white mt-0 mb-5">
                                View and manage teacher and student attendance records.
                            </p>
                        </Col>
                    </Row>
                </Container>
            </div>
            <Container className="mt--7" fluid>
                <Row>
                    <Col>
                        <Card className="shadow">
                            <CardHeader className="border-0 d-flex justify-content-between align-items-center">
                                <h3 className="mb-0">Attendance Records</h3>
                                <div className="d-flex align-items-center flex-wrap" style={{ gap: '8px' }}>
                                    <Button
                                        size="sm"
                                        color={view === 'teacher' ? 'primary' : 'secondary'}
                                        onClick={() => handleViewChange('teacher')}
                                    >
                                        Staff
                                    </Button>
                                    <Button
                                        size="sm"
                                        color={view === 'student' ? 'primary' : 'secondary'}
                                        onClick={() => handleViewChange('student')}
                                    >
                                        Student
                                    </Button>
                                    <Button
                                        size="sm"
                                        color="success"
                                        onClick={() => handleAddAttendanceToggle()}
                                    >
                                        <FaPlus /> Add Attendance
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardBody>
                                <Row className="mb-3">
                                    <Col md="4">
                                        <FormGroup>
                                            <Label>Filter by Date</Label>
                                            <Input
                                                type="date"
                                                value={filterDate}
                                                onChange={handleFilterDateChange}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="4">
                                        <FormGroup>
                                            <Label>Filter by Name</Label>
                                            <Input
                                                type="text"
                                                placeholder="Enter student name"
                                                value={filterName}
                                                onChange={handleFilterNameChange}
                                            />
                                        </FormGroup>
                                    </Col>
                                    {view === 'student' && (
                                        <Col md="4">
                                            <FormGroup>
                                                <Label>Filter by Class</Label>
                                                <Input
                                                    type="select"
                                                    value={filterClass}
                                                    onChange={handleClassSelect}
                                                >
                                                    <option value="">All Classes</option>
                                                    {classes.map((cls) => (
                                                        <option key={cls._id} value={cls._id}>
                                                            {cls.className}
                                                        </option>
                                                    ))}
                                                </Input>
                                            </FormGroup>
                                        </Col>
                                    )}
                                </Row>
                                {view === 'teacher' ? (
                                    <>
                                        <Nav tabs>
                                            <NavItem>
                                                <NavLink
                                                    className={activeTeacherTab === 'full-time' ? 'active' : ''}
                                                    onClick={() => setActiveTeacherTab('full-time')}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    FULL TIME
                                                </NavLink>
                                            </NavItem>
                                            <NavItem>
                                                <NavLink
                                                    className={activeTeacherTab === 'part-time' ? 'active' : ''}
                                                    onClick={() => setActiveTeacherTab('part-time')}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    PART TIME
                                                </NavLink>
                                            </NavItem>
                                            <NavItem>
                                                <NavLink
                                                    className={activeTeacherTab === 'visitor' ? 'active' : ''}
                                                    onClick={() => setActiveTeacherTab('visitor')}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    VISITOR
                                                </NavLink>
                                            </NavItem>
                                        </Nav>
                                        <TeacherAttendance
                                            filterDate={filterDate}
                                            filterName={filterName}
                                            refreshAttendance={refreshAttendance}
                                            setRefreshAttendance={setRefreshAttendance}
                                            teacherTypeFilter={activeTeacherTab}
                                        />
                                    </>
                                ) : (
                                    <StudentAttendance
                                        filterDate={filterDate}
                                        filterName={filterName}
                                        filterClass={filterClass}
                                        classes={classes}
                                        onEditAttendance={handleAddAttendanceToggle}
                                        refreshAttendance={refreshAttendance}
                                    />
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
            <ToastContainer />

            <Modal isOpen={addStudentAttendanceModalOpen} toggle={() => handleAddAttendanceToggle()}>
                <ModalHeader toggle={() => handleAddAttendanceToggle()}>
                    {isEditing ? 'Edit Student Attendance' : 'Add Student Attendance'}
                </ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Label>Student Name</Label>
                        <Input
                            type="select"
                            name="studentId"
                            value={newStudentAttendance.studentId}
                            onChange={handleNewStudentAttendanceChange}
                            disabled={isEditing}
                        >
                            <option value="">Select Student</option>
                            {studentNames.map((student) => (
                                <option key={student._id} value={student._id}>
                                    {student.fullName}
                                </option>
                            ))}
                        </Input>
                    </FormGroup>
                    <FormGroup>
                        <Label>Date</Label>
                        <Input
                            type="date"
                            name="date"
                            value={newStudentAttendance.date}
                            onChange={handleNewStudentAttendanceChange}
                            max={new Date().toISOString().split('T')[0]}
                        />
                    </FormGroup>
                        <FormGroup>
                            <Label>Status</Label>
                            <Input
                                type="select"
                                name="status"
                                value={newStudentAttendance.status}
                                onChange={handleNewStudentAttendanceChange}
                            >
                                <option value="Present">Present</option>
                                <option value="Absent">Absent</option>
                                <option value="Late">Late</option>
                                <option value="Half Day">Half Day</option>
                            </Input>
                        </FormGroup>
                        <FormGroup>
                            <Label>Check-In Time</Label>
                            <Input
                                type="time"
                                name="checkInTime"
                                value={newStudentAttendance.checkInTime}
                                onChange={handleNewStudentAttendanceChange}
                                disabled={newStudentAttendance.status === 'Absent'}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>Check-Out Time</Label>
                            <Input
                                type="time"
                                name="checkOutTime"
                                value={newStudentAttendance.checkOutTime}
                                onChange={handleNewStudentAttendanceChange}
                                disabled={newStudentAttendance.status === 'Absent'}
                            />
                        </FormGroup>
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={handleAddStudentAttendanceSubmit} disabled={loading}>
                        {loading ? 'Submitting...' : isEditing ? 'Update Attendance' : 'Add Attendance'}
                    </Button>
                    <Button color="secondary" onClick={() => handleAddAttendanceToggle()}>
                        Cancel
                    </Button>
                </ModalFooter>
            </Modal>

            <Modal isOpen={addTeacherAttendanceModalOpen} toggle={() => handleAddAttendanceToggle()}>
                <ModalHeader toggle={() => handleAddAttendanceToggle()}>
                    {isEditing ? 'Edit Teacher Attendance' : 'Add Teacher Attendance'}
                </ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Label>Teacher Name</Label>
                        <Input
                            type="select"
                            name="teacherId"
                            value={newTeacherAttendance.teacherId}
                            onChange={handleNewTeacherAttendanceChange}
                            disabled={isEditing}
                        >
                            <option value="">Select Teacher</option>
                            {teacherNames.map((teacher) => (
                                <option key={teacher._id} value={teacher._id}>
                                    {teacher.fullName}
                                </option>
                            ))}
                        </Input>
                    </FormGroup>
                    <FormGroup>
                        <Label>Date</Label>
                        <Input
                            type="date"
                            name="date"
                            value={newTeacherAttendance.date}
                            onChange={handleNewTeacherAttendanceChange}
                            max={new Date().toISOString().split('T')[0]}
                        />
                    </FormGroup>
                        <FormGroup>
                            <Label>Status</Label>
                            <Input
                                type="select"
                                name="status"
                                value={newTeacherAttendance.status}
                                onChange={handleNewTeacherAttendanceChange}
                            >
                                <option value="Present">Present</option>
                                <option value="Absent">Absent</option>
                                <option value="Late">Late</option>
                                <option value="Half Day">Half Day</option>
                            </Input>
                        </FormGroup>
                        <FormGroup>
                            <Label>Check-In Time</Label>
                            <Input
                                type="time"
                                name="checkInTime"
                                value={newTeacherAttendance.checkInTime}
                                onChange={handleNewTeacherAttendanceChange}
                                disabled={newTeacherAttendance.status === 'Absent'}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>Check-Out Time</Label>
                            <Input
                                type="time"
                                name="checkOutTime"
                                value={newTeacherAttendance.checkOutTime}
                                onChange={handleNewTeacherAttendanceChange}
                                disabled={newTeacherAttendance.status === 'Absent'}
                            />
                        </FormGroup>
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={handleAddTeacherAttendanceSubmit} disabled={loading}>
                        {loading ? 'Submitting...' : isEditing ? 'Update Attendance' : 'Add Attendance'}
                    </Button>
                    <Button color="secondary" onClick={() => handleAddAttendanceToggle()}>
                        Cancel
                    </Button>
                </ModalFooter>
            </Modal>
            <Footer />
        </>
    );
};

export default AttendanceOverview;